import { JourneyType, ProcessPage, ProcessPageParams } from '@adapters/navigation/page';
import { Injectable, Signal, WritableSignal, computed, signal } from '@angular/core';
import { Account } from '@core/model/domain/account';
import { AccountAnalysisState } from '@core/model/domain/accountAnalysisState';
import { Bank } from '@core/model/domain/bank';
import { RegionalBankPayload } from '@core/service/digital-factory/bank/types/dto/connectionFormDto';

type StoreDictionnary = {
  USER_ID: string;
  OPEN_BANKING_USER_TOKEN: string;
  PROCESS_PAGE: ProcessPage;
  TERMS_VERSION: string;
  CGU_VERSION: string;
  FOUND_ACCOUNTS: Account[];
  SELECTED_CONNECTION_ID: string;
  SELECTED_BANK: Bank;
  TAGGING_INIT_DATA_IN_BASE64: string;
  PROCESS_PAGE_PARAMS: ProcessPageParams<ProcessPage>;
  PROCESS_PAGE_HISTORY: Array<ProcessPage>;
  SHOULD_TAG_ERROR_PAGE: boolean;
  SHOULD_IGNORE_NEXT_POPSTATE: boolean;
  ACCOUNT_ANALYSIS_STATE: AccountAnalysisState;
  IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON: boolean;
  BANKS: Bank[];
  SELECTED_ACCOUNTS: Account[];
  JOURNEY_TYPE: JourneyType;
  IS_WEBAUTH_REDIRECTION_GOING_TO_PARTNER: boolean;
  WEBAUTH_BANK_URL: string | null;
  WEBAUTH_JOURNEY_KEY: string;
  WEBAUTH_REGIONAL_BANK_PAYLOAD: RegionalBankPayload | null;
  WEBAUTH_AWAITING_DEVICE_CHOICE: boolean;
  /* Transient UI state: the go-back confirmation popin is displayed. Never persisted. */
  IS_GO_BACK_CONFIRMATION_DISPLAYED: boolean;
};

type StoreVariableKeys = keyof StoreDictionnary;

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  private readonly cachedVariables: {
    [Key in StoreVariableKeys]: WritableSignal<StoreDictionnary[Key] | null>;
  } = {
    OPEN_BANKING_USER_TOKEN: this.getSignalFromStorage('OPEN_BANKING_USER_TOKEN'),
    PROCESS_PAGE: this.getSignalFromStorage('PROCESS_PAGE'),
    USER_ID: this.getSignalFromStorage('USER_ID'),
    FOUND_ACCOUNTS: this.getSignalFromStorage('FOUND_ACCOUNTS'),
    SELECTED_CONNECTION_ID: this.getSignalFromStorage('SELECTED_CONNECTION_ID'),
    SELECTED_BANK: this.getSignalFromStorage('SELECTED_BANK'),
    PROCESS_PAGE_PARAMS: this.getSignalFromStorage('PROCESS_PAGE_PARAMS'),
    TERMS_VERSION: this.getSignalFromStorage('TERMS_VERSION'),
    CGU_VERSION: this.getSignalFromStorage('CGU_VERSION'),
    TAGGING_INIT_DATA_IN_BASE64: this.getSignalFromStorage('TAGGING_INIT_DATA_IN_BASE64'),
    PROCESS_PAGE_HISTORY: this.getSignalFromStorage('PROCESS_PAGE_HISTORY'),
    SHOULD_TAG_ERROR_PAGE: this.getSignalFromStorage('SHOULD_TAG_ERROR_PAGE'),
    SHOULD_IGNORE_NEXT_POPSTATE: this.getSignalFromStorage('SHOULD_IGNORE_NEXT_POPSTATE'),
    ACCOUNT_ANALYSIS_STATE: this.getSignalFromStorage('ACCOUNT_ANALYSIS_STATE'),
    IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON: this.getSignalFromStorage('IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON'),
    BANKS: this.getSignalFromStorage('BANKS'),
    SELECTED_ACCOUNTS: this.getSignalFromStorage('SELECTED_ACCOUNTS'),
    JOURNEY_TYPE: this.getSignalFromStorage('JOURNEY_TYPE'),
    IS_WEBAUTH_REDIRECTION_GOING_TO_PARTNER: this.getSignalFromStorage(
      'IS_WEBAUTH_REDIRECTION_GOING_TO_PARTNER'
    ),
    WEBAUTH_BANK_URL: this.getSignalFromStorage('WEBAUTH_BANK_URL'),
    WEBAUTH_JOURNEY_KEY: this.getSignalFromStorage('WEBAUTH_JOURNEY_KEY'),
    WEBAUTH_REGIONAL_BANK_PAYLOAD: this.getSignalFromStorage('WEBAUTH_REGIONAL_BANK_PAYLOAD'),
    WEBAUTH_AWAITING_DEVICE_CHOICE: this.getSignalFromStorage('WEBAUTH_AWAITING_DEVICE_CHOICE'),
    IS_GO_BACK_CONFIRMATION_DISPLAYED: this.getSignalFromStorage('IS_GO_BACK_CONFIRMATION_DISPLAYED'),
  };

  setVariable<TKey extends StoreVariableKeys>(key: TKey, value: StoreDictionnary[TKey]): void {
    this.setKeyInStorage(key, value);
    this.setKeyInCache(key, value);
  }

  getVariable<TKey extends StoreVariableKeys>(key: TKey): StoreDictionnary[TKey] | null {
    const value = this.cachedVariables[key]();
    return value;
  }

  getVariableOrThrow<TKey extends StoreVariableKeys>(key: TKey): StoreDictionnary[TKey] {
    const variable = this.getVariable(key);
    if (variable == null) {
      throw new Error(`Could not retrieve ${key} because not defined.`);
    }

    return variable;
  }

  select<TKey extends StoreVariableKeys>(key: TKey): Signal<StoreDictionnary[TKey] | null> {
    return this.cachedVariables[key].asReadonly();
  }

  selectOrThrow<TKey extends StoreVariableKeys>(key: TKey): Signal<StoreDictionnary[TKey]> {
    return computed(() => {
      const value = this.select(key)();
      if (value == null) {
        throw new Error(`Could not retrieve ${key} because not defined.`);
      }

      return value;
    });
  }

  reset() {
    for (const key in this.cachedVariables) {
      this.clearKeyInStorage(key as StoreVariableKeys);
      this.clearKeyInCache(key as StoreVariableKeys);
    }
  }

  private getSignalFromStorage<TKey extends StoreVariableKeys>(
    key: TKey
  ): WritableSignal<StoreDictionnary[TKey] | null> {
    const storage = this.getStorageFromKey(key);
    if (storage == 'none') {
      return signal(null);
    }

    const selectedStorage = storage == 'localStorage' ? localStorage : sessionStorage;
    const storageKeyName = this.getStorageKeyNameFromKey(key);
    const value = selectedStorage.getItem(storageKeyName);

    if (value == null) {
      return signal(null);
    }

    const parsedValue = JSON.parse(value);
    return signal(parsedValue as StoreDictionnary[TKey]);
  }

  private getStorageKeyNameFromKey(key: StoreVariableKeys): string {
    switch (key) {
      case 'OPEN_BANKING_USER_TOKEN':
        return 'open-banking-user-token';
      case 'FOUND_ACCOUNTS':
        return 'pobo-found-accounts';
      case 'SELECTED_CONNECTION_ID':
        return 'open-banking-selected-connection-id';
      case 'SELECTED_BANK':
        return 'pobo-selected-bank';
      case 'TAGGING_INIT_DATA_IN_BASE64':
        return 'open-banking-bank-tagging-init-data';
      case 'PROCESS_PAGE_PARAMS':
        return 'open-banking-process-page-params';
      case 'USER_ID':
        return 'open-banking-userId';
      case 'PROCESS_PAGE':
        return 'pobo-process-page';
      case 'PROCESS_PAGE_HISTORY':
        return 'open-banking-process-page-history';
      case 'SHOULD_TAG_ERROR_PAGE':
        return 'open-banking-should-tag-error-page';
      case 'SHOULD_IGNORE_NEXT_POPSTATE':
        return 'open-banking-ignore-next-popstate';
      case 'ACCOUNT_ANALYSIS_STATE':
        return 'open-banking-selected-accounts-case-state';
      case 'IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON':
        return 'open-banking-is-popstate-triggered-via-cancel';
      case 'BANKS':
        return 'open-banking-banks';
      case 'SELECTED_ACCOUNTS':
        return 'open-banking-selected-accounts-id';
      case 'CGU_VERSION':
      case 'TERMS_VERSION':
      case 'IS_GO_BACK_CONFIRMATION_DISPLAYED':
        return 'NOT_PROVIDED';
      case 'JOURNEY_TYPE':
        return 'open-banking-user-journey-type';
      case 'IS_WEBAUTH_REDIRECTION_GOING_TO_PARTNER':
        return 'open-banking-user-is-legacy-redirection';
      case 'WEBAUTH_BANK_URL':
        return 'open-banking-webauth-bank-url';
      case 'WEBAUTH_JOURNEY_KEY':
        return 'open-banking-webauth-journey-key';
      case 'WEBAUTH_REGIONAL_BANK_PAYLOAD':
        return 'open-banking-webauth-regional-bank-payload';
      case 'WEBAUTH_AWAITING_DEVICE_CHOICE':
        return 'open-banking-webauth-awaiting-device-choice';
      default:
        return this.caseSanitizer(key);
    }
  }

  private getStorageFromKey(key: StoreVariableKeys): 'localStorage' | 'sessionStorage' | 'none' {
    switch (key) {
      case 'TAGGING_INIT_DATA_IN_BASE64':
      case 'OPEN_BANKING_USER_TOKEN':
      case 'FOUND_ACCOUNTS':
      case 'SELECTED_CONNECTION_ID':
      case 'SELECTED_BANK':
      case 'PROCESS_PAGE_PARAMS':
      case 'USER_ID':
      case 'PROCESS_PAGE':
      case 'PROCESS_PAGE_HISTORY':
      case 'SHOULD_TAG_ERROR_PAGE':
      case 'SHOULD_IGNORE_NEXT_POPSTATE':
      case 'ACCOUNT_ANALYSIS_STATE':
      case 'IS_BACK_NAVIGATION_FROM_IN_APP_BUTTON':
      case 'BANKS':
      case 'SELECTED_ACCOUNTS':
      case 'JOURNEY_TYPE':
      case 'IS_WEBAUTH_REDIRECTION_GOING_TO_PARTNER':
      case 'WEBAUTH_BANK_URL':
      case 'WEBAUTH_JOURNEY_KEY':
      case 'WEBAUTH_REGIONAL_BANK_PAYLOAD':
      case 'WEBAUTH_AWAITING_DEVICE_CHOICE':
        return 'sessionStorage';

      case 'CGU_VERSION':
      case 'TERMS_VERSION':
      case 'IS_GO_BACK_CONFIRMATION_DISPLAYED':
        return 'none';
      default:
        return this.caseSanitizer(key);
    }
  }

  private clearKeyInStorage<TKey extends StoreVariableKeys>(key: TKey): void {
    const storage = this.getStorageFromKey(key);
    if (storage == 'sessionStorage') {
      sessionStorage.removeItem(this.getStorageKeyNameFromKey(key));
    } else if (storage == 'localStorage') {
      localStorage.removeItem(this.getStorageKeyNameFromKey(key));
    }
  }

  private clearKeyInCache<TKey extends StoreVariableKeys>(key: TKey): void {
    this.cachedVariables[key].set(null);
  }

  private setKeyInCache<TKey extends StoreVariableKeys>(key: TKey, value: StoreDictionnary[TKey]) {
    const cachedVariable = this.cachedVariables[key];
    cachedVariable.set(value);
  }

  private setKeyInStorage<TKey extends StoreVariableKeys>(key: TKey, value: StoreDictionnary[TKey]) {
    const storage = this.getStorageFromKey(key);
    if (storage == 'localStorage' || storage == 'sessionStorage') {
      const selectedStorage = storage == 'localStorage' ? localStorage : sessionStorage;

      const storageKeyName = this.getStorageKeyNameFromKey(key);
      const stringifiedValue = JSON.stringify(value);
      selectedStorage.setItem(storageKeyName, stringifiedValue);
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private caseSanitizer(_input: 'TERMS_VERSION' | 'CGU_VERSION'): never {
    throw new Error('Should never go here');
  }
}
